import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';

export type UpdateDecisionType = 'assessed' | 'consider' | 'reject' | 'accepted' | 'to_interview' | 'malpractice' | 'interview' | 'revisit' | 'probe_further' | 'save_for_future' | 'request_more_info';

interface UpdateDecisionPayload {
  assessment_response_ids: number[];
  decision: UpdateDecisionType;
}

interface UpdateDecisionResponse {
  message: string;
  data: {
    updated_count: number;
    decision: string;
    bucket: string;
  };
}

/**
 * Hook for updating assessment response decisions
 */
export const useUpdateCandidateDecision = () => {
  const { openToast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (payload: UpdateDecisionPayload) => {
      const response = await apiClient.post<UpdateDecisionResponse>(
        '/candidate/dashboard/update-decision/',
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      openToast({
        show: true,
        heading: "Success",
        text: data.message,
        type: "success",
        timeOut: 5000,
      });

      // Invalidate relevant queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['reviewTalentsCandidates'] });
      queryClient.invalidateQueries({ queryKey: ['reviewTalentsSummary'] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update decision';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    updateDecision: mutation.mutate,
    isUpdating: mutation.isPending,
    error: mutation.error,
  };
};
